import { LONG_DASH } from '@/constants/stringVars';
import { text } from '@/theme/colors/palettes';
import { TCortexOrganizationInsightsV2, TCortexRecommendationV2 } from '@/types/Cortex.types';
import { cortexPriorityColor, formatCortexCategory, orderSkillsToFocusOnV2 } from '@/utils/cortexInsights';
import { capitalize } from '@/utils/stringUtils';
import { HStack, Text, VStack } from '@chakra-ui/react';
import React from 'react';
import ExpandableText from '@/components/atoms/ExpandableText';

// Faint priority-tinted card backgrounds (matches the V2 "MyOmnia Recommendations" design).
const PRIORITY_BACKGROUND: Record<string, string> = {
  CRITICAL: '#FDF2F2',
  HIGH: '#FEF6EF',
};
const DEFAULT_PRIORITY_BACKGROUND = '#F3F4F8';

interface CardProps {
  recommendation: TCortexRecommendationV2;
  largerFont?: boolean;
  // When false, the body text always renders in full (no "Read more" toggle). Defaults to true.
  expandable?: boolean;
}

export const RecommendationCardV2 = ({ recommendation, largerFont, expandable = true }: CardProps) => {
  const priorityColor = cortexPriorityColor(recommendation.priority);
  const backgroundColor =
    PRIORITY_BACKGROUND[(recommendation.priority ?? '').toUpperCase()] ?? DEFAULT_PRIORITY_BACKGROUND;
  const fontSize = largerFont ? '16px' : '14px';

  return (
    <VStack
      alignItems={'start'}
      gap={1.5}
      width={'100%'}
      backgroundColor={backgroundColor}
      borderRadius={'10px'}
      padding={'14px 16px'}
    >
      <HStack width={'100%'} justifyContent={'space-between'}>
        <Text variant={'urbanistExtraBoldSmall'} color={priorityColor}>
          {formatCortexCategory(recommendation.category)}
        </Text>
        <Text variant={'urbanistExtraBoldSmall'} color={priorityColor}>
          {(recommendation.priority ?? '').toUpperCase()}
        </Text>
      </HStack>
      {expandable ? (
        <ExpandableText text={recommendation.text} variant={'urbanistBold'} fontSize={fontSize} />
      ) : (
        <Text variant={'urbanistBold'} fontSize={fontSize}>
          {recommendation.text}
        </Text>
      )}
      {recommendation.expectedOutcome ? (
        <Text variant={'urbanistMedium'} color={text.mediumBlue} fontSize={fontSize}>
          {LONG_DASH} {capitalize(recommendation.expectedOutcome)}
        </Text>
      ) : null}
    </VStack>
  );
};

interface Props {
  insights: TCortexOrganizationInsightsV2;
}

const RecommendationsDisplayV2 = ({ insights }: Props) => {
  const recommendations = orderSkillsToFocusOnV2(insights);

  if (recommendations.length === 0) {
    return (
      <Text variant={'urbanistMediumRegular'} color={'text.mediumBlue'} marginBottom={'20px'}>
        No recommendations available yet.
      </Text>
    );
  }

  return (
    <VStack gap={'12px'} width={'100%'}>
      {recommendations.map((recommendation, i) => (
        <RecommendationCardV2 key={`${recommendation.category}-${i}`} recommendation={recommendation} />
      ))}
    </VStack>
  );
};

export default RecommendationsDisplayV2;
